import { Box, CardMedia, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { COLORS, unit } from '@hcr/ui'
import { isNotNull, isNotUndefined, isNull } from '@hcr/utils'

import { useHoliday } from '../../../contexts'
import { useDestinationDetailsQuery, useRoomDetailsQuery } from '../../../hooks'
import { Path } from '../../../models'
import {
  createAccommodationGuestsString,
  createRoomDetailsContent,
  createRoomNameString,
  isFlyAccommodation,
  isOwnerUsage,
  to,
} from '../../../utils'
import {
  BookingSummaryAccommodation,
  ExpandableText,
  LayoutNavigationBack,
  LayoutNavigationBackError500,
  Metadata,
} from '../../common'

import { BookingDetailsFallback } from './BookingDetailsFallback'
import { BookingDetailsLoading } from './BookingDetailsLoading'
import { BookingTimeshareIntroductionInfo, BookingTermsAndConditions } from './common'

const BACK_NAVIGATION_PATH = to(Path.Booking)

const roomDetailsContent = createRoomDetailsContent()

export const BookingDetails: FC = () => {
  const { t } = useTranslation()
  const {
    data: { accommodation, destination },
    ...holiday
  } = useHoliday()

  const destinationDetails = useDestinationDetailsQuery(
    { destinationId: String(destination?.destinationId) },
    { enabled: isNotUndefined(destination) }
  )

  const roomDetails = useRoomDetailsQuery(
    { destinationId: String(destination?.destinationId), roomId: String(accommodation?.room_type_id) },
    {
      enabled:
        isNotUndefined(destination) && isNotUndefined(accommodation) && isNotUndefined(accommodation.room_type_id),
    }
  )

  if (holiday.isError) {
    return <LayoutNavigationBackError500 to={BACK_NAVIGATION_PATH} />
  }

  if (destinationDetails.isError || roomDetails.isError) {
    return <BookingDetailsFallback />
  }

  if (holiday.isSuccess && isNotUndefined(accommodation) && isNull(accommodation.accommodation_id)) {
    return <Navigate to={BACK_NAVIGATION_PATH} replace />
  }

  if (holiday.isSuccess && isNotUndefined(accommodation) && destinationDetails.isSuccess && roomDetails.isSuccess) {
    const hasTimeshareIntroductionInfo = isFlyAccommodation(accommodation)

    return (
      <LayoutNavigationBack to={BACK_NAVIGATION_PATH} arrowVariant='outlined'>
        <Metadata title={t('page-title.booking-details')} />
        <Box bgcolor={COLORS.white} minHeight='100vh'>
          <CardMedia image={roomDetails.data.imageUrl} sx={{ height: unit(71) }} />
          <Stack paddingTop={unit(7)} paddingX={unit(8)} paddingBottom={unit(4.5)}>
            <Typography component='span' variant='titleS' color={COLORS.grey[700]}>
              {t('booking-details.booking-details')}
            </Typography>
            <Typography component='h1' variant='headlineM' marginTop={unit(1)}>
              {createRoomNameString(roomDetails.data)}
            </Typography>
            {isNotNull(roomDetails.data.realEstateName) && (
              <Typography component='h2' variant='titleS' marginTop={unit(2)}>
                {roomDetails.data.realEstateName}
              </Typography>
            )}
            {isNotNull(roomDetails.data.description) && (
              <Box marginTop={unit(4)}>
                <ExpandableText component='h2' value={roomDetails.data.description} />
              </Box>
            )}
          </Stack>
          <Stack gap={unit(3)} padding={unit(3)}>
            <BookingSummaryAccommodation
              accommodationId={accommodation.accommodation_id}
              accommodationStatus={accommodation.status}
              bookingDate={accommodation.booking_date}
              checkInDate={accommodation.start_date}
              checkInHour={roomDetails.data.checkInHour}
              checkOutDate={accommodation.end_date}
              checkOutHour={roomDetails.data.checkOutHour}
              destinationName={destinationDetails.data.name}
              guests={createAccommodationGuestsString(accommodation)}
              paymentStatus={accommodation.payment_status}
              isOwnerUsage={isOwnerUsage(accommodation)}
              price={accommodation.total_amount}
            />
            {/* // TODO 128: Restore price summaries on booking details when a bug in HotelLinx is fixed */}
            {/* //  https://hcrdevelopment.atlassian.net/browse/SII-128 */}
            {/* // <BookingSummaryPrice items={accommodation.prices} total={accommodation.total_amount} /> */}

            {hasTimeshareIntroductionInfo && <BookingTimeshareIntroductionInfo />}

            <BookingTermsAndConditions />

            {roomDetails.data.content.map(roomDetailsContent)}
          </Stack>
        </Box>
      </LayoutNavigationBack>
    )
  }

  return <BookingDetailsLoading backNavigationPath={BACK_NAVIGATION_PATH} />
}
